import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { restaurantAPI } from '../../services/api';
import socketService from '../../services/socket';
import { useAuth } from '../../context/AuthContext';

// Toast notification types
const TOAST_TYPES = {
  SUCCESS: 'success',
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error'
};

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function Earnings() {
  const { restaurant } = useAuth();
  const [earnings, setEarnings] = useState(null);
  const [stats, setStats] = useState(null);
  const [instantPayoutInfo, setInstantPayoutInfo] = useState(null);
  const [payoutPrediction, setPayoutPrediction] = useState(null);
  const [loadingPrediction, setLoadingPrediction] = useState(false);
  const [loading, setLoading] = useState(true);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [payoutAmount, setPayoutAmount] = useState('');
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [exportingEarnings, setExportingEarnings] = useState(false);
  const [transactions, setTransactions] = useState(null);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionPage, setTransactionPage] = useState(1);
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  // Date range filter
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [customRangeData, setCustomRangeData] = useState(null);
  const [loadingCustomRange, setLoadingCustomRange] = useState(false);

  // Add toast notification
  const addToast = useCallback((message, type = TOAST_TYPES.INFO, duration = 5000) => {
    const id = ++toastIdRef.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  // Remove toast
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    fetchAllData();
  }, []);

  // Debounced refetch. Earnings page subscribes to 6 different socket
  // events, all of which call fetchAllData. When a payout completes, the
  // backend fires payout_status_update + payout_success + earnings_update
  // within the same tick, causing 3-4 parallel refetches. Debouncing
  // collapses them into a single API call.
  const debouncedFetchTimerRef = useRef(null);
  const scheduleDebouncedFetch = useCallback(() => {
    if (debouncedFetchTimerRef.current !== null) {
      clearTimeout(debouncedFetchTimerRef.current);
    }
    debouncedFetchTimerRef.current = setTimeout(() => {
      debouncedFetchTimerRef.current = null;
      fetchAllData();
    }, 400);
  }, []);

  // Socket listeners for real-time earnings and payout updates
  useEffect(() => {
    if (!restaurant?._id) return;

    // Connect to socket and join restaurant room
    socketService.connect();
    socketService.joinRestaurantRoom(restaurant._id);

    // Listen for earnings update (after order delivered)
    const unsubEarningsUpdate = socketService.on('earnings_update', (data) => {
      console.log('💰 Earnings: Earnings update received:', data);
      addToast(`New earnings from Order #${data.orderNumber}: ₹${data.netAmount?.toFixed(0) || data.grossAmount?.toFixed(0) || 0}`, TOAST_TYPES.SUCCESS);
      scheduleDebouncedFetch();
    });

    // Listen for payout created
    const unsubPayoutCreated = socketService.on('payout_created', (data) => {
      console.log('📄 Earnings: Payout created:', data);
      addToast(`Payout of ₹${data.amount?.toFixed(0) || 0} has been scheduled`, TOAST_TYPES.INFO);
      scheduleDebouncedFetch();
    });

    // Listen for payout status update
    const unsubPayoutStatusUpdate = socketService.on('payout_status_update', (data) => {
      console.log('🔄 Earnings: Payout status update:', data);
      const statusMessages = {
        processing: 'Payout is being processed',
        completed: 'Payout completed successfully!',
        failed: 'Payout failed',
        pending: 'Payout is pending'
      };
      const message = statusMessages[data.status] || `Payout status: ${data.status}`;
      const type = data.status === 'completed' ? TOAST_TYPES.SUCCESS :
                   data.status === 'failed' ? TOAST_TYPES.ERROR : TOAST_TYPES.INFO;
      addToast(`${message} - ₹${data.amount?.toFixed(0) || 0}`, type);
      scheduleDebouncedFetch();
    });

    // Listen for payout success
    const unsubPayoutSuccess = socketService.on('payout_success', (data) => {
      console.log('✅ Earnings: Payout success:', data);
      addToast(`🎉 Payout of ₹${data.amount?.toFixed(0) || 0} successful! Ref: ${data.transactionRef || 'N/A'}`, TOAST_TYPES.SUCCESS, 8000);
      scheduleDebouncedFetch();
    });

    // Listen for payout failure
    const unsubPayoutFailure = socketService.on('payout_failure', (data) => {
      console.log('❌ Earnings: Payout failure:', data);
      addToast(`Payout of ₹${data.amount?.toFixed(0) || 0} failed: ${data.error || 'Unknown error'}`, TOAST_TYPES.ERROR, 10000);
      scheduleDebouncedFetch();
    });

    // Listen for payout schedule notification
    const unsubPayoutSchedule = socketService.on('payout_schedule_notification', (data) => {
      console.log('📅 Earnings: Payout schedule notification:', data);
      addToast(`Upcoming payout of ₹${data.amount?.toFixed(0) || 0} scheduled for ${data.scheduledDate ? new Date(data.scheduledDate).toLocaleDateString() : 'soon'}`, TOAST_TYPES.INFO, 10000);
    });

    return () => {
      unsubEarningsUpdate();
      unsubPayoutCreated();
      unsubPayoutStatusUpdate();
      unsubPayoutSuccess();
      unsubPayoutFailure();
      unsubPayoutSchedule();
      if (debouncedFetchTimerRef.current !== null) {
        clearTimeout(debouncedFetchTimerRef.current);
        debouncedFetchTimerRef.current = null;
      }
    };
  }, [restaurant?._id, addToast, scheduleDebouncedFetch]);

  const fetchCustomRangeEarnings = async (startDate, endDate) => {
    try {
      setLoadingCustomRange(true);
      const res = await restaurantAPI.getEarnings({ startDate, endDate });
      const data = res?.data?.data || res?.data || {};
      setCustomRangeData(data.customRange || { earnings: 0, orders: 0 });
    } catch (e) {
      console.error('Failed to fetch custom range:', e);
    } finally {
      setLoadingCustomRange(false);
    }
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    if (period === 'custom' && customStartDate && customEndDate) {
      fetchCustomRangeEarnings(customStartDate, customEndDate);
    }
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      setSelectedPeriod('custom');
      fetchCustomRangeEarnings(customStartDate, customEndDate);
    }
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      // Fetch all data in parallel like Flutter app
      const [earningsRes, statsRes, payoutInfoRes, predictionRes] = await Promise.all([
        restaurantAPI.getEarnings().catch(e => ({ data: null })),
        restaurantAPI.getMyStats().catch(e => ({ data: null })),
        restaurantAPI.getInstantPayoutInfo().catch(e => ({ data: null })),
        restaurantAPI.getPayoutPrediction().catch(e => ({ data: null }))
      ]);

      // Handle earnings response
      const earningsData = earningsRes?.data?.data || earningsRes?.data || {};
      setEarnings(earningsData);

      // Handle stats response
      const statsData = statsRes?.data?.data || statsRes?.data || {};
      setStats(statsData);

      // Handle instant payout info response
      const payoutInfoData = payoutInfoRes?.data?.data || payoutInfoRes?.data || {};
      setInstantPayoutInfo(payoutInfoData);

      // Handle payout prediction response
      const predictionData = predictionRes?.data?.data || predictionRes?.data || null;
      setPayoutPrediction(predictionData);

    } catch (error) {
      console.error('Failed to fetch earnings data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInstantPayout = async () => {
    const amount = parseFloat(payoutAmount);
    const minAmount = instantPayoutInfo?.minAmount || 500;
    const maxAmount = instantPayoutInfo?.maxRequestableAmount || instantPayoutInfo?.availableBalance || 0;

    if (!amount || amount < minAmount) {
      alert(`Minimum amount is ₹${minAmount}`);
      return;
    }
    if (amount > maxAmount) {
      alert(`Maximum amount is ₹${maxAmount}`);
      return;
    }

    try {
      setRequestingPayout(true);
      await restaurantAPI.requestInstantPayout(amount);
      alert('Instant payout requested successfully!');
      setShowPayoutModal(false);
      setPayoutAmount('');
      fetchAllData();
    } catch (error) {
      console.error('Failed to request payout:', error);
      alert(error.response?.data?.message || 'Failed to request payout. Please try again.');
    } finally {
      setRequestingPayout(false);
    }
  };

  const handleCancelPayout = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this instant payout request?')) return;

    try {
      await restaurantAPI.cancelInstantPayout(requestId);
      alert('Request cancelled successfully');
      fetchAllData();
    } catch (error) {
      console.error('Failed to cancel payout:', error);
      alert('Failed to cancel request. Please try again.');
    }
  };

  const handleExportEarnings = async (format = 'csv') => {
    try {
      setExportingEarnings(true);
      const response = await restaurantAPI.exportEarnings({ format });

      // Create blob and download
      const blob = new Blob([response.data], {
        type: format === 'csv' ? 'text/csv' : 'application/json'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `earnings_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export earnings:', error);
      alert('Failed to export earnings. Please try again.');
    } finally {
      setExportingEarnings(false);
    }
  };

  const fetchTransactions = async (page = 1) => {
    try {
      setTransactionsLoading(true);
      const response = await restaurantAPI.getTransactionLedger({ page, limit: 50 });
      const data = response?.data?.data || response?.data || {};

      if (page === 1) {
        setTransactions(data);
      } else {
        // Append more transactions for pagination
        setTransactions(prev => ({
          ...data,
          transactions: [...(prev?.transactions || []), ...(data.transactions || [])]
        }));
      }
      setTransactionPage(page);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setTransactionsLoading(false);
    }
  };

  // Fetch transactions when tab changes to transactions
  useEffect(() => {
    if (activeTab === 'transactions' && !transactions) {
      fetchTransactions(1);
    }
  }, [activeTab]);

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Compute combined values from stats and earnings (like Flutter app)
  const totalEarnings = stats?.totalRevenue || earnings?.totalEarnings || 0;
  const todayEarnings = stats?.todayRevenue || earnings?.todayEarnings || 0;
  const weeklyEarningsTotal = stats?.weekRevenue || earnings?.weeklyEarnings || 0;
  const monthlyEarnings = stats?.monthRevenue || earnings?.monthlyEarnings || 0;
  // Use nullish coalescing (??) not logical OR (||) — 0 and negative values are valid balances
  const pendingPayout = earnings?.pendingPayout ?? earnings?.remainingPayout ?? (totalEarnings - (earnings?.completedPayout ?? 0));
  const completedPayout = earnings?.completedPayout ?? earnings?.totalPaidOut ?? 0;
  // Net non-order adjustments (admin credits − debits − subscription settlements).
  // Shown so order earnings + adjustments reconcile with what was actually paid.
  const adjustmentsNet = earnings?.adjustmentsNet ?? 0;
  // Instant payout requested but not yet disbursed (pending admin approval) — reserved, not paid.
  const reservedInstant = earnings?.reservedInstantPayout ?? 0;
  const availableBalance = instantPayoutInfo?.availableBalance ?? pendingPayout;

  // Subscription deduction info from backend
  const subscriptionDeduction = earnings?.subscriptionDeduction || null;
  const actualPendingPayout = subscriptionDeduction?.needsDeduction
    ? subscriptionDeduction.adjustedPayoutAmount
    : pendingPayout;

  // Get subscription status message and styling
  const getSubscriptionStatusInfo = () => {
    if (!subscriptionDeduction?.needsDeduction) return null;

    const status = subscriptionDeduction.subscriptionStatus;
    switch (status) {
      case 'trial_expired':
        return {
          title: 'Trial Period Ended',
          icon: '⏰',
          bgColor: 'rgba(245, 158, 11, 0.1)',
          borderColor: 'rgba(245, 158, 11, 0.3)',
          textColor: '#f59e0b',
          description: 'Your free trial has ended. Subscription fee will be deducted from your next payout.'
        };
      case 'period_ended':
        return {
          title: 'Subscription Renewal Due',
          icon: '🔄',
          bgColor: 'rgba(59, 130, 246, 0.1)',
          borderColor: 'rgba(59, 130, 246, 0.3)',
          textColor: '#3b82f6',
          description: 'Your subscription period has ended. Renewal fee will be auto-deducted from your next payout.'
        };
      case 'expired':
        return {
          title: 'Subscription Expired',
          icon: '⚠️',
          bgColor: 'rgba(239, 68, 68, 0.1)',
          borderColor: 'rgba(239, 68, 68, 0.3)',
          textColor: '#ef4444',
          description: 'Your subscription has expired. Fee will be deducted to reactivate your subscription.'
        };
      default:
        return null;
    }
  };

  const subscriptionStatusInfo = getSubscriptionStatusInfo();

  // Calculate fee for instant payout preview
  const feePercentage = instantPayoutInfo?.feePercentage || 5; // Match backend default (INSTANT_PAYOUT_FEE_PERCENTAGE)
  const calculateFee = (amount) => (parseFloat(amount) || 0) * feePercentage / 100;
  const calculateNetAmount = (amount) => (parseFloat(amount) || 0) - calculateFee(amount);

  // Chart data for weekly earnings.
  // Memoised so that renders where `earnings.weeklyEarnings` didn't change
  // reuse the same data-object identity, preventing react-chartjs-2 from
  // performing unnecessary Chart.js update cycles (each of which allocates
  // new canvas-level animation state).
  const weeklyChartData = useMemo(() => ({
    labels: earnings?.weeklyEarnings?.map?.(d => d.day) || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Daily Earnings',
        data: earnings?.weeklyEarnings?.map?.(d => d.amount) || [0, 0, 0, 0, 0, 0, 0],
        fill: true,
        backgroundColor: 'rgba(79, 70, 229, 0.1)',
        borderColor: '#4f46e5',
        tension: 0.4,
        pointBackgroundColor: '#4f46e5',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [earnings?.weeklyEarnings]);

  // Memoise chart options — no runtime deps, so compute once and keep
  // the same object identity across all renders. react-chartjs-2 diffs
  // options by identity and will rebuild the chart if it sees a new
  // reference.
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: '#1f2937',
        titleFont: { size: 14 },
        bodyFont: { size: 13 },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (context) => `₹${context.raw.toLocaleString()}`
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: { size: 12 }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: { size: 12 },
          callback: (value) => `₹${value}`
        }
      }
    }
  }), []);

  if (loading) return <div className="loading">Loading earnings...</div>;

  return (
    <div style={{ background: '#F8FAFC', minHeight: '100vh' }}>
      <div className="container">
        {/* Total Earnings Header */}
        <div style={{
          background: '#FF6961',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-2xl)',
          marginBottom: 'var(--space-xl)',
          textAlign: 'center',
          color: 'white'
        }} className="fade-in">
          <div style={{ fontSize: 'var(--font-size-sm)', opacity: 0.9, marginBottom: 'var(--space-sm)' }}>
            Total Earnings
          </div>
          <div style={{ fontSize: '2.5rem', fontWeight: '800', marginBottom: 'var(--space-lg)' }}>
            {formatCurrency(totalEarnings)}
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 'var(--space-xl)',
            fontSize: 'var(--font-size-sm)',
            opacity: 0.95
          }}>
            <div>
              <div style={{ fontWeight: '600' }}>Pending Payout</div>
              <div>{formatCurrency(pendingPayout)}</div>
              {subscriptionDeduction?.needsDeduction && (
                <div style={{
                  fontSize: 'var(--font-size-xs)',
                  opacity: 0.8,
                  marginTop: '4px'
                }}>
                  After sub. deduction: {formatCurrency(actualPendingPayout)}
                </div>
              )}
            </div>
            {reservedInstant > 0 && (
              <div>
                <div style={{ fontWeight: '600' }}>Requested</div>
                <div>{formatCurrency(reservedInstant)}</div>
                <div style={{ fontSize: 'var(--font-size-xs)', opacity: 0.8, marginTop: '4px' }}>
                  awaiting payout
                </div>
              </div>
            )}
            {adjustmentsNet !== 0 && (
              <div title="Admin credits/debits & subscription settlements applied to your payouts, beyond order revenue">
                <div style={{ fontWeight: '600' }}>Adjustments</div>
                <div>{adjustmentsNet > 0 ? '+' : '−'}{formatCurrency(Math.abs(adjustmentsNet))}</div>
                <div style={{ fontSize: 'var(--font-size-xs)', opacity: 0.8, marginTop: '4px' }}>
                  credits/debits applied
                </div>
              </div>
            )}
            <div>
              <div style={{ fontWeight: '600' }}>Total Paid</div>
              <div>{formatCurrency(completedPayout)}</div>
            </div>
          </div>
        </div>

        {/* Summary Cards Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 'var(--space-md)',
          marginBottom: 'var(--space-xl)'
        }}>
          {/* Period filter chips */}
          {[
            { key: 'today', label: 'Today', icon: '💵' },
            { key: 'week', label: 'This Week', icon: '📅' },
            { key: 'month', label: 'This Month', icon: '📊' },
            { key: 'custom', label: 'Custom Range', icon: '📆' },
          ].map(p => (
            <div key={p.key}
              onClick={() => handlePeriodChange(p.key)}
              style={{
                background: selectedPeriod === p.key
                  ? 'linear-gradient(135deg, #2563eb, #1d4ed8)'
                  : 'rgba(241, 245, 249, 1)',
                border: selectedPeriod === p.key ? 'none' : '1px solid var(--gray-200)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-lg)',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                color: selectedPeriod === p.key ? 'white' : 'inherit'
              }} className="fade-in">
              <div style={{ fontSize: '2rem', marginBottom: 'var(--space-sm)' }}>{p.icon}</div>
              <div style={{
                fontSize: 'var(--font-size-2xl)', fontWeight: '800',
                color: selectedPeriod === p.key ? 'white' : 'var(--gray-800)',
                marginBottom: 'var(--space-xs)'
              }}>
                {p.key === 'today' ? formatCurrency(todayEarnings)
                  : p.key === 'week' ? formatCurrency(weeklyEarningsTotal)
                  : p.key === 'month' ? formatCurrency(monthlyEarnings)
                  : loadingCustomRange ? '...'
                  : formatCurrency(customRangeData?.earnings || 0)}
              </div>
              <div style={{
                fontSize: 'var(--font-size-sm)',
                color: selectedPeriod === p.key ? 'rgba(255,255,255,0.8)' : 'var(--gray-600)'
              }}>
                {p.label}
                {p.key !== 'custom' && ` (${
                  p.key === 'today' ? (stats?.todayOrders || 0)
                  : p.key === 'week' ? (stats?.weekOrders || 0)
                  : (stats?.monthOrders || 0)
                } orders)`}
                {p.key === 'custom' && customRangeData && ` (${customRangeData.orders} orders)`}
              </div>
            </div>
          ))}
        </div>

        {/* Custom date picker (shown when Custom selected) */}
        {selectedPeriod === 'custom' && (
          <div style={{
            display: 'flex', gap: 'var(--space-md)', alignItems: 'center',
            marginBottom: 'var(--space-xl)', flexWrap: 'wrap'
          }} className="fade-in">
            <input type="date" value={customStartDate}
              onChange={e => setCustomStartDate(e.target.value)}
              style={{
                padding: 'var(--space-sm) var(--space-md)',
                border: '1px solid var(--gray-300)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)'
              }}
            />
            <span style={{ color: 'var(--gray-500)' }}>to</span>
            <input type="date" value={customEndDate}
              onChange={e => setCustomEndDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              style={{
                padding: 'var(--space-sm) var(--space-md)',
                border: '1px solid var(--gray-300)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)'
              }}
            />
            <button onClick={handleCustomDateApply}
              disabled={!customStartDate || !customEndDate || loadingCustomRange}
              style={{
                padding: 'var(--space-sm) var(--space-lg)',
                background: 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                opacity: (!customStartDate || !customEndDate) ? 0.5 : 1
              }}>
              {loadingCustomRange ? 'Loading...' : 'Apply'}
            </button>
          </div>
        )}

        {/* Tax Information Card - Flutter parity */}
        {(earnings?.tdsInfo || earnings?.gstBreakdown || stats?.tdsInfo || stats?.gstBreakdown) && (
          <div style={{
            background: 'rgba(249, 115, 22, 0.05)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-lg)',
            marginBottom: 'var(--space-xl)',
            border: '1px solid rgba(249, 115, 22, 0.2)'
          }} className="fade-in">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)' }}>
              <span style={{ fontSize: '20px' }}>📋</span>
              <span style={{ fontWeight: '700', fontSize: 'var(--font-size-md)', color: '#c2410c' }}>
                Tax Information
              </span>
            </div>

            {/* TDS Info */}
            <div style={{
              background: 'white',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-md)',
              marginBottom: 'var(--space-sm)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-xs)' }}>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
                  TDS Deducted (Section 194-O)
                </span>
                <span style={{ fontWeight: '600', color: '#c2410c' }}>
                  {formatCurrency(earnings?.tdsInfo?.totalTdsDeducted || stats?.tdsInfo?.totalTdsDeducted || 0)}
                </span>
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', fontStyle: 'italic' }}>
                {earnings?.tdsInfo?.rate || stats?.tdsInfo?.rate || 1}% TDS deducted at source from each order
              </div>
            </div>

            {/* GST Info - if applicable */}
            {(earnings?.gstBreakdown?.totalGstCollected > 0 || stats?.gstBreakdown?.totalGstCollected > 0) && (
              <div style={{
                background: 'white',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-md)',
                marginBottom: 'var(--space-sm)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-xs)' }}>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
                    GST Collected ({(earnings?.gstBreakdown?.foodGstRate || stats?.gstBreakdown?.foodGstRate || 5).toFixed(0)}%)
                  </span>
                  <span style={{ fontWeight: '600', color: '#2563eb' }}>
                    {formatCurrency(earnings?.gstBreakdown?.totalGstCollected || stats?.gstBreakdown?.totalGstCollected || 0)}
                  </span>
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', fontStyle: 'italic' }}>
                  GST collected from customers (to be remitted to govt)
                </div>

                {/* Net After GST */}
                {(earnings?.gstBreakdown?.netAfterGstRemittance > 0 || stats?.gstBreakdown?.netAfterGstRemittance > 0) && (
                  <>
                    <div style={{ borderTop: '1px dashed #e5e7eb', margin: 'var(--space-sm) 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-xs)' }}>
                      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)', fontWeight: '500' }}>
                        Net After GST Remittance
                      </span>
                      <span style={{ fontWeight: '700', color: '#16a34a' }}>
                        {formatCurrency(earnings?.gstBreakdown?.netAfterGstRemittance || stats?.gstBreakdown?.netAfterGstRemittance || 0)}
                      </span>
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: '#16a34a', fontStyle: 'italic' }}>
                      Your actual earnings after remitting GST
                    </div>
                  </>
                )}
              </div>
            )}

            <div style={{ fontSize: 'var(--font-size-xs)', color: '#c2410c', marginTop: 'var(--space-xs)' }}>
              Note: TDS (0.1%) is estimated per order and deducted on aggregate at payout time under Section 194-O.
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: 'var(--space-sm)',
          marginBottom: 'var(--space-xl)',
          background: 'white',
          padding: 'var(--space-xs)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'transactions', label: 'Transactions' },
            { id: 'payouts', label: 'Payouts' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                padding: 'var(--space-md)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                background: activeTab === tab.id ? '#FF6961' : 'transparent',
                color: activeTab === tab.id ? 'white' : 'var(--gray-700)',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Payout Info Card */}
            <div style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-lg)',
              marginBottom: 'var(--space-lg)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }} className="fade-in">
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px'
                }}>📅</div>
                <div>
                  <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)', fontWeight: '700', color: 'var(--gray-900)' }}>
                    Payout Schedule
                  </h3>
                  <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
                    Weekly payout every {earnings?.payoutInfo?.payoutDayName || 'Monday'}
                  </p>
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 'var(--space-md)',
                marginBottom: 'var(--space-md)'
              }}>
                <div style={{
                  padding: 'var(--space-md)',
                  background: '#F8FAFC',
                  borderRadius: 'var(--radius-md)',
                  textAlign: 'center'
                }}>
                  <div style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-xs)' }}>
                    Next Payout
                  </div>
                  <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: '700', color: 'var(--gray-900)' }}>
                    {formatDate(earnings?.payoutInfo?.nextPayoutDate || earnings?.nextPayoutDate)}
                  </div>
                </div>
                <div style={{
                  padding: 'var(--space-md)',
                  background: 'rgba(245, 158, 11, 0.1)',
                  borderRadius: 'var(--radius-md)',
                  textAlign: 'center'
                }}>
                  <div style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-xs)' }}>
                    Days Until Payout
                  </div>
                  <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: '700', color: '#f59e0b' }}>
                    {earnings?.payoutInfo?.daysUntilNextPayout ?? 7} days
                  </div>
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 'var(--space-md)',
                marginBottom: 'var(--space-md)'
              }}>
                <div style={{
                  padding: 'var(--space-md)',
                  background: '#F8FAFC',
                  borderRadius: 'var(--radius-md)'
                }}>
                  <div style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-xs)' }}>
                    Last Payout
                  </div>
                  <div style={{ fontSize: 'var(--font-size-md)', fontWeight: '600', color: 'var(--gray-900)' }}>
                    {earnings?.payoutInfo?.lastPayoutAmount > 0
                      ? formatCurrency(earnings.payoutInfo.lastPayoutAmount)
                      : 'No payouts yet'}
                  </div>
                  {earnings?.payoutInfo?.lastPayoutDate && (
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', marginTop: '2px' }}>
                      {formatDate(earnings.payoutInfo.lastPayoutDate)}
                    </div>
                  )}
                </div>
                <div style={{
                  padding: 'var(--space-md)',
                  background: '#F8FAFC',
                  borderRadius: 'var(--radius-md)'
                }}>
                  <div style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-xs)' }}>
                    Minimum Payout
                  </div>
                  <div style={{ fontSize: 'var(--font-size-md)', fontWeight: '600', color: 'var(--gray-900)' }}>
                    {formatCurrency(earnings?.payoutInfo?.minimumPayoutAmount || 100)}
                  </div>
                </div>
              </div>

              {/* Meets Minimum Status */}
              <div style={{
                padding: 'var(--space-sm) var(--space-md)',
                background: earnings?.payoutInfo?.meetsMinimum !== false ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                borderRadius: 'var(--radius-md)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)'
              }}>
                <span>{earnings?.payoutInfo?.meetsMinimum !== false ? '✅' : '⚠️'}</span>
                <span style={{
                  fontSize: 'var(--font-size-sm)',
                  color: earnings?.payoutInfo?.meetsMinimum !== false ? '#10b981' : '#ef4444',
                  fontWeight: '500'
                }}>
                  {earnings?.payoutInfo?.meetsMinimum !== false
                    ? 'You meet the minimum payout requirement'
                    : `Need ${formatCurrency((earnings?.payoutInfo?.minimumPayoutAmount || 100) - pendingPayout)} more to meet minimum`}
                </span>
              </div>

              {earnings?.payoutInfo?.message && (
                <div style={{
                  marginTop: 'var(--space-md)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--gray-600)',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}>
                  {earnings.payoutInfo.message}
                </div>
              )}
            </div>

            {/* Subscription Deduction Card */}
            {subscriptionStatusInfo && (
              <div style={{
                background: subscriptionStatusInfo.bgColor,
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-lg)',
                marginBottom: 'var(--space-lg)',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: `1px solid ${subscriptionStatusInfo.borderColor}`
              }} className="fade-in">
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    flexShrink: 0
                  }}>{subscriptionStatusInfo.icon}</div>
                  <div>
                    <h3 style={{
                      margin: 0,
                      fontSize: 'var(--font-size-lg)',
                      fontWeight: '700',
                      color: subscriptionStatusInfo.textColor
                    }}>
                      {subscriptionStatusInfo.title}
                    </h3>
                    <p style={{
                      margin: 0,
                      marginTop: '4px',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--gray-600)'
                    }}>
                      {subscriptionStatusInfo.description}
                    </p>
                  </div>
                </div>

                {/* Deduction Details */}
                <div style={{
                  background: 'white',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-md)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 'var(--space-sm)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--gray-600)'
                  }}>
                    <span>Subscription Fee</span>
                    <span style={{ fontWeight: '600', color: subscriptionStatusInfo.textColor }}>
                      {formatCurrency(subscriptionDeduction.subscriptionFee)}
                    </span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 'var(--space-sm)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--gray-600)'
                  }}>
                    <span>Current Pending</span>
                    <span>{formatCurrency(pendingPayout)}</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingTop: 'var(--space-sm)',
                    borderTop: '1px dashed #E5E7EB',
                    fontSize: 'var(--font-size-md)',
                    fontWeight: '700',
                    color: subscriptionDeduction.canDeduct ? '#10b981' : '#ef4444'
                  }}>
                    <span>After Deduction</span>
                    <span>{formatCurrency(subscriptionDeduction.adjustedPayoutAmount)}</span>
                  </div>
                </div>

                {/* Insufficient Balance Warning */}
                {subscriptionDeduction.insufficientBalance && (
                  <div style={{
                    marginTop: 'var(--space-md)',
                    padding: 'var(--space-sm) var(--space-md)',
                    background: 'rgba(239, 68, 68, 0.1)',
                    borderRadius: 'var(--radius-md)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)'
                  }}>
                    <span>⚠️</span>
                    <span style={{ fontSize: 'var(--font-size-sm)', color: '#ef4444', fontWeight: '500' }}>
                      Insufficient balance! Shortfall: {formatCurrency(subscriptionDeduction.shortfall)}
                    </span>
                  </div>
                )}

                {/* Period Info */}
                {subscriptionDeduction.periodStart && subscriptionDeduction.periodEnd && (
                  <div style={{
                    marginTop: 'var(--space-md)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--gray-500)',
                    textAlign: 'center'
                  }}>
                    Subscription period: {formatDate(subscriptionDeduction.periodStart)} - {formatDate(subscriptionDeduction.periodEnd)}
                  </div>
                )}
              </div>
            )}

            {/* Payout Prediction Card */}
            {payoutPrediction && (
              <div style={{
                background: 'white',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-lg)',
                marginBottom: 'var(--space-lg)',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(79, 70, 229, 0.2)'
              }} className="fade-in">
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'rgba(79, 70, 229, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>🔮</div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)', fontWeight: '700', color: 'var(--gray-900)' }}>
                      Payout Prediction
                    </h3>
                    <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
                      AI-powered estimate for your next payout
                    </p>
                  </div>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 'var(--space-md)',
                  marginBottom: 'var(--space-md)'
                }}>
                  <div style={{
                    padding: 'var(--space-md)',
                    background: 'rgba(79, 70, 229, 0.05)',
                    borderRadius: 'var(--radius-md)',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', marginBottom: 'var(--space-xs)' }}>
                      Predicted Amount
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: '800', color: '#4f46e5' }}>
                      {formatCurrency(payoutPrediction.predictedAmount || 0)}
                    </div>
                  </div>
                  <div style={{
                    padding: 'var(--space-md)',
                    background: `rgba(${(payoutPrediction.confidence || 0) >= 0.8 ? '16, 185, 129' : (payoutPrediction.confidence || 0) >= 0.6 ? '245, 158, 11' : '239, 68, 68'}, 0.05)`,
                    borderRadius: 'var(--radius-md)',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', marginBottom: 'var(--space-xs)' }}>
                      Confidence
                    </div>
                    <div style={{
                      display: 'inline-block',
                      padding: '4px 12px',
                      borderRadius: 'var(--radius-full)',
                      fontSize: 'var(--font-size-md)',
                      fontWeight: '700',
                      color: 'white',
                      background: (payoutPrediction.confidence || 0) >= 0.8 ? '#10b981' : (payoutPrediction.confidence || 0) >= 0.6 ? '#f59e0b' : '#ef4444'
                    }}>
                      {Math.round((payoutPrediction.confidence || 0) * 100)}% confident
                    </div>
                  </div>
                </div>

                <div style={{
                  padding: 'var(--space-sm) var(--space-md)',
                  background: '#F8FAFC',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: 'var(--font-size-sm)'
                }}>
                  <span style={{ color: 'var(--gray-600)' }}>
                    📅 Next payout: {formatDate(payoutPrediction.nextPayoutDate)}
                  </span>
                  <span style={{ color: 'var(--gray-600)' }}>
                    💰 Pending: {formatCurrency(payoutPrediction.pendingAmount || pendingPayout)}
                  </span>
                </div>

                {payoutPrediction.message && (
                  <div style={{
                    marginTop: 'var(--space-md)',
                    padding: 'var(--space-sm) var(--space-md)',
                    background: 'rgba(59, 130, 246, 0.1)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--gray-600)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)'
                  }}>
                    <span>💡</span>
                    <span>{payoutPrediction.message}</span>
                  </div>
                )}
              </div>
            )}

            {/* Export Earnings Button */}
            <div style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-lg)',
              marginBottom: 'var(--space-lg)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }} className="fade-in">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px'
                  }}>📊</div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)', fontWeight: '700', color: 'var(--gray-900)' }}>
                      Export Earnings
                    </h3>
                    <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
                      Download your earnings data
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                  <button
                    onClick={() => handleExportEarnings('csv')}
                    disabled={exportingEarnings}
                    style={{
                      padding: 'var(--space-sm) var(--space-md)',
                      border: '1px solid #10b981',
                      borderRadius: 'var(--radius-md)',
                      background: 'white',
                      color: '#10b981',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: '600',
                      cursor: exportingEarnings ? 'not-allowed' : 'pointer',
                      opacity: exportingEarnings ? 0.5 : 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    📥 CSV
                  </button>
                  <button
                    onClick={() => handleExportEarnings('json')}
                    disabled={exportingEarnings}
                    style={{
                      padding: 'var(--space-sm) var(--space-md)',
                      border: '1px solid #4f46e5',
                      borderRadius: 'var(--radius-md)',
                      background: 'white',
                      color: '#4f46e5',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: '600',
                      cursor: exportingEarnings ? 'not-allowed' : 'pointer',
                      opacity: exportingEarnings ? 0.5 : 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    📥 JSON
                  </button>
                </div>
              </div>
            </div>

            {/* Instant Payout Section */}
            <div style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-lg)',
              marginBottom: 'var(--space-lg)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.2)'
            }} className="fade-in">
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: 'rgba(245, 158, 11, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px'
                }}>⚡</div>
                <div>
                  <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)', fontWeight: '700', color: 'var(--gray-900)' }}>
                    Instant Payout
                  </h3>
                  <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
                    Get your money within 24 hours
                  </p>
                </div>
              </div>

              {/* Pending Request Status */}
              {instantPayoutInfo?.pendingRequest && (
                <div style={{
                  padding: 'var(--space-md)',
                  background: instantPayoutInfo.pendingRequest.status === 'approved' ? 'rgba(34, 197, 94, 0.1)' :
                             instantPayoutInfo.pendingRequest.status === 'processing' ? 'rgba(168, 85, 247, 0.1)' :
                             'rgba(59, 130, 246, 0.1)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--space-md)',
                  border: `1px solid ${instantPayoutInfo.pendingRequest.status === 'approved' ? 'rgba(34, 197, 94, 0.3)' :
                                        instantPayoutInfo.pendingRequest.status === 'processing' ? 'rgba(168, 85, 247, 0.3)' :
                                        'rgba(59, 130, 246, 0.3)'}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ margin: 0, fontWeight: '600', color: 'var(--gray-900)' }}>
                        {instantPayoutInfo.pendingRequest.status === 'approved' ? '✓ Request Approved' :
                         instantPayoutInfo.pendingRequest.status === 'processing' ? '⟳ Transfer Processing' :
                         '⏳ Pending Request'}
                      </p>
                      <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
                        {formatCurrency(instantPayoutInfo.pendingRequest.amount)} - {instantPayoutInfo.pendingRequest.statusMessage || 'Awaiting approval'}
                      </p>
                    </div>
                    {instantPayoutInfo.pendingRequest.status === 'pending' && (
                      <button
                        onClick={() => handleCancelPayout(instantPayoutInfo.pendingRequest.requestId)}
                        style={{
                          padding: '6px 12px',
                          border: 'none',
                          borderRadius: '6px',
                          background: 'rgba(239, 68, 68, 0.1)',
                          color: '#EF4444',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Recent Rejection Notification */}
              {instantPayoutInfo?.recentRejection && !instantPayoutInfo?.pendingRequest && (
                <div style={{
                  padding: 'var(--space-md)',
                  background: 'rgba(239, 68, 68, 0.1)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--space-md)',
                  border: '1px solid rgba(239, 68, 68, 0.3)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-md)' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: 'rgba(239, 68, 68, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      flexShrink: 0
                    }}>❌</div>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: '600', color: '#EF4444' }}>Request Rejected</p>
                      <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
                        Amount: {formatCurrency(instantPayoutInfo.recentRejection.amount)}
                      </p>
                      <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-sm)', color: '#EF4444' }}>
                        Reason: {instantPayoutInfo.recentRejection.reason}
                      </p>
                      {instantPayoutInfo.recentRejection.rejectedAt && (
                        <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
                          {formatDate(instantPayoutInfo.recentRejection.rejectedAt)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Failure Notification */}
              {instantPayoutInfo?.recentFailure && !instantPayoutInfo?.pendingRequest && !instantPayoutInfo?.recentRejection && (
                <div style={{
                  padding: 'var(--space-md)',
                  background: 'rgba(239, 68, 68, 0.1)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--space-md)',
                  border: '1px solid rgba(239, 68, 68, 0.3)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-md)' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: 'rgba(239, 68, 68, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      flexShrink: 0
                    }}>⚠️</div>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: '600', color: '#EF4444' }}>Transfer Failed</p>
                      <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
                        Amount: {formatCurrency(instantPayoutInfo.recentFailure.amount)}
                      </p>
                      <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-sm)', color: '#EF4444' }}>
                        Reason: {instantPayoutInfo.recentFailure.reason}
                      </p>
                      {instantPayoutInfo.recentFailure.failedAt && (
                        <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
                          {formatDate(instantPayoutInfo.recentFailure.failedAt)}
                        </p>
                      )}
                      <p style={{ margin: '8px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', fontStyle: 'italic' }}>
                        The amount has been returned to your available balance
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Can Request - Show Available Balance */}
              {(instantPayoutInfo?.canRequest || !instantPayoutInfo?.pendingRequest) && (
                <>
                  <div style={{
                    padding: 'var(--space-md)',
                    background: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 'var(--space-md)'
                  }}>
                    <div style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-xs)' }}>
                      Available Balance
                    </div>
                    <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: '800', color: '#10b981' }}>
                      {formatCurrency(availableBalance)}
                    </div>
                  </div>

                  <button
                    onClick={() => setShowPayoutModal(true)}
                    disabled={!instantPayoutInfo?.canRequest && availableBalance < (instantPayoutInfo?.minAmount || 500)}
                    style={{
                      width: '100%',
                      padding: 'var(--space-md)',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      background: '#f59e0b',
                      color: 'white',
                      fontSize: 'var(--font-size-md)',
                      fontWeight: '700',
                      cursor: (instantPayoutInfo?.canRequest || availableBalance >= (instantPayoutInfo?.minAmount || 500)) ? 'pointer' : 'not-allowed',
                      opacity: (instantPayoutInfo?.canRequest || availableBalance >= (instantPayoutInfo?.minAmount || 500)) ? 1 : 0.5,
                      transition: 'all 0.2s ease',
                      marginBottom: 'var(--space-sm)'
                    }}
                  >
                    ⚡ Request Instant Payout
                  </button>
                </>
              )}

              <div style={{
                padding: 'var(--space-sm) var(--space-md)',
                background: 'rgba(245, 158, 11, 0.1)',
                borderRadius: 'var(--radius-md)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)'
              }}>
                <span>⚠️</span>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
                  {feePercentage}% processing fee applies. Min. {formatCurrency(instantPayoutInfo?.minAmount || 500)} required
                </span>
              </div>
            </div>

            {/* Weekly Chart */}
            <div style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-lg)',
              marginBottom: 'var(--space-lg)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }} className="fade-in">
              <h3 style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: '700',
                marginBottom: 'var(--space-lg)',
                color: 'var(--gray-900)'
              }}>
                Weekly Earnings Trend
              </h3>
              <div style={{ height: '300px' }}>
                <Line data={weeklyChartData} options={chartOptions} />
              </div>
            </div>

            {/* Recent Payouts Section */}
            {(earnings?.payouts?.length > 0 || earnings?.payoutHistory?.length > 0) && (
              <div style={{
                background: 'white',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-lg)',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }} className="fade-in">
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 'var(--space-lg)'
                }}>
                  <h3 style={{
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: '700',
                    margin: 0,
                    color: 'var(--gray-900)'
                  }}>
                    Recent Payouts
                  </h3>
                  <button
                    onClick={() => setActiveTab('payouts')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#FF6961',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    View All →
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
                  {(earnings.payouts || earnings.payoutHistory || []).slice(0, 3).map((payout, index) => {
                    const isSubscriptionDeduction = payout.purpose === 'subscription_deduction';
                    const payoutDate = payout.createdAt || payout.date;

                    return (
                      <div key={payout._id || payout.id || index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 'var(--space-md)',
                        background: isSubscriptionDeduction ? 'rgba(168, 85, 247, 0.05)' : '#F8FAFC',
                        borderRadius: 'var(--radius-md)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                          <div style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '8px',
                            background: isSubscriptionDeduction ? 'rgba(168, 85, 247, 0.1)' :
                                       payout.status === 'completed' || payout.status === 'success' ? 'rgba(16, 185, 129, 0.1)' :
                                       'rgba(245, 158, 11, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px'
                          }}>
                            {isSubscriptionDeduction ? '🔄' :
                             payout.status === 'completed' || payout.status === 'success' ? '✓' : '⏳'}
                          </div>
                          <div>
                            <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: '600', color: 'var(--gray-900)' }}>
                              {isSubscriptionDeduction ? 'Subscription Renewal' : formatCurrency(payout.amount)}
                            </div>
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
                              {formatDate(payoutDate)}
                            </div>
                          </div>
                        </div>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: 'var(--radius-full)',
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: '600',
                          background: isSubscriptionDeduction ? 'rgba(168, 85, 247, 0.1)' :
                                     payout.status === 'completed' || payout.status === 'success' ? 'rgba(16, 185, 129, 0.1)' :
                                     'rgba(245, 158, 11, 0.1)',
                          color: isSubscriptionDeduction ? '#a855f7' :
                                payout.status === 'completed' || payout.status === 'success' ? '#10b981' :
                                '#f59e0b'
                        }}>
                          {isSubscriptionDeduction ? 'AUTO' :
                           payout.status === 'success' ? 'Done' :
                           payout.status?.charAt(0).toUpperCase() + payout.status?.slice(1)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div style={{
            background: 'white',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-lg)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }} className="fade-in">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-lg)' }}>
              <h3 style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: '700',
                margin: 0,
                color: 'var(--gray-900)'
              }}>
                📒 Transaction Ledger
              </h3>
              {transactions?.summary && (
                <div style={{
                  padding: 'var(--space-sm) var(--space-md)',
                  background: transactions.summary.currentPendingBalance >= 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: '600',
                  color: transactions.summary.currentPendingBalance >= 0 ? '#10b981' : '#ef4444'
                }}>
                  Current Balance: {formatCurrency(transactions.summary.currentPendingBalance)}
                </div>
              )}
            </div>

            {/* Summary Cards */}
            {transactions?.summary && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 'var(--space-md)',
                marginBottom: 'var(--space-xl)'
              }}>
                <div style={{
                  padding: 'var(--space-md)',
                  background: 'rgba(16, 185, 129, 0.1)',
                  borderRadius: 'var(--radius-md)',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-600)', marginBottom: '4px' }}>Total Earnings</div>
                  <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: '700', color: '#10b981' }}>
                    +{formatCurrency(transactions.summary.totalEarnings)}
                  </div>
                </div>
                <div style={{
                  padding: 'var(--space-md)',
                  background: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: 'var(--radius-md)',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-600)', marginBottom: '4px' }}>Total Payouts</div>
                  <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: '700', color: '#3b82f6' }}>
                    {formatCurrency(transactions.summary.totalPayouts)}
                  </div>
                </div>
              </div>
            )}

            {/* Transactions List */}
            {transactionsLoading && !transactions ? (
              <div style={{ textAlign: 'center', padding: 'var(--space-2xl)', color: 'var(--gray-600)' }}>
                <div style={{ fontSize: '2rem', marginBottom: 'var(--space-md)' }}>⏳</div>
                <p>Loading transactions...</p>
              </div>
            ) : transactions?.transactions?.length > 0 ? (
              <>
                {/* Table Header */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 100px 100px 120px',
                  gap: 'var(--space-sm)',
                  padding: 'var(--space-sm) var(--space-md)',
                  background: '#F1F5F9',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--space-sm)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: '600',
                  color: 'var(--gray-600)',
                  textTransform: 'uppercase'
                }}>
                  <span>Transaction</span>
                  <span style={{ textAlign: 'right' }}>Credit</span>
                  <span style={{ textAlign: 'right' }}>Debit</span>
                  <span style={{ textAlign: 'right' }}>Balance</span>
                </div>

                {/* Transaction Rows */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {transactions.transactions.map((txn, index) => {
                    const isCredit = txn.type === 'order_earning' || txn.type === 'manual_credit';
                    const isDeduction = txn.type === 'subscription_deduction' || txn.type === 'manual_debit';
                    const isPayout = txn.type === 'payout';
                    const isManual = txn.type === 'manual_credit' || txn.type === 'manual_debit';

                    return (
                      <div key={txn._id || index} style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 100px 100px 120px',
                        gap: 'var(--space-sm)',
                        padding: 'var(--space-md)',
                        background: isManual ? 'rgba(245, 158, 11, 0.03)' :
                                   isDeduction ? 'rgba(168, 85, 247, 0.03)' :
                                   isPayout ? 'rgba(59, 130, 246, 0.03)' :
                                   'rgba(16, 185, 129, 0.03)',
                        borderRadius: 'var(--radius-sm)',
                        borderLeft: `3px solid ${isManual ? '#f59e0b' : isDeduction ? '#a855f7' : isPayout ? '#3b82f6' : '#10b981'}`,
                        alignItems: 'center'
                      }}>
                        {/* Transaction Details */}
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                            <span style={{ fontSize: '16px' }}>
                              {isManual ? (txn.type === 'manual_credit' ? '➕' : '➖') : isCredit ? '🍽️' : isDeduction ? '🔄' : '💸'}
                            </span>
                            <div>
                              <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: '600', color: 'var(--gray-900)' }}>
                                {txn.description}
                              </div>
                              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
                                {formatDate(txn.date)}
                                {isCredit && txn.details?.tdsDeducted > 0 && (
                                  <span style={{ marginLeft: '8px', color: '#ef4444' }}>
                                    TDS: -₹{txn.details.tdsDeducted.toFixed(1)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Credit */}
                        <div style={{ textAlign: 'right' }}>
                          {isCredit ? (
                            <span style={{ color: '#10b981', fontWeight: '600', fontSize: 'var(--font-size-sm)' }}>
                              +{formatCurrency(txn.credit)}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--gray-300)' }}>-</span>
                          )}
                        </div>

                        {/* Debit */}
                        <div style={{ textAlign: 'right' }}>
                          {(isPayout || isDeduction) ? (
                            <span style={{ color: isDeduction ? '#a855f7' : '#3b82f6', fontWeight: '600', fontSize: 'var(--font-size-sm)' }}>
                              -{formatCurrency(txn.debit)}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--gray-300)' }}>-</span>
                          )}
                        </div>

                        {/* Running Balance */}
                        <div style={{ textAlign: 'right' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: 'var(--radius-sm)',
                            background: txn.runningBalance >= 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                            color: txn.runningBalance >= 0 ? '#10b981' : '#ef4444',
                            fontWeight: '700',
                            fontSize: 'var(--font-size-sm)'
                          }}>
                            {formatCurrency(txn.runningBalance)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Load More Button */}
                {transactions.pagination?.hasMore && (
                  <button
                    onClick={() => fetchTransactions(transactionPage + 1)}
                    disabled={transactionsLoading}
                    style={{
                      width: '100%',
                      marginTop: 'var(--space-lg)',
                      padding: 'var(--space-md)',
                      border: '2px dashed #E5E7EB',
                      borderRadius: 'var(--radius-md)',
                      background: 'transparent',
                      color: transactionsLoading ? 'var(--gray-400)' : 'var(--gray-600)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: '600',
                      cursor: transactionsLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {transactionsLoading ? 'Loading...' : `Load More (${transactions.pagination.totalTransactions - transactions.transactions.length} remaining)`}
                  </button>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: 'var(--space-2xl)', color: 'var(--gray-600)' }}>
                <div style={{ fontSize: '3rem', marginBottom: 'var(--space-md)' }}>📒</div>
                <p>No transactions yet</p>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-500)' }}>
                  Your order earnings, payouts, and deductions will appear here
                </p>
              </div>
            )}

            {/* Legend */}
            <div style={{
              display: 'flex',
              gap: 'var(--space-lg)',
              marginTop: 'var(--space-xl)',
              padding: 'var(--space-md)',
              background: '#F8FAFC',
              borderRadius: 'var(--radius-md)',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', fontSize: 'var(--font-size-xs)' }}>
                <span style={{ width: '12px', height: '12px', borderRadius: '2px', background: '#10b981' }}></span>
                <span style={{ color: 'var(--gray-600)' }}>Order Earnings</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', fontSize: 'var(--font-size-xs)' }}>
                <span style={{ width: '12px', height: '12px', borderRadius: '2px', background: '#3b82f6' }}></span>
                <span style={{ color: 'var(--gray-600)' }}>Payouts</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', fontSize: 'var(--font-size-xs)' }}>
                <span style={{ width: '12px', height: '12px', borderRadius: '2px', background: '#a855f7' }}></span>
                <span style={{ color: 'var(--gray-600)' }}>Subscription Deductions</span>
              </div>
            </div>
          </div>
        )}

        {/* Payouts Tab */}
        {activeTab === 'payouts' && (
          <div style={{
            background: 'white',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-lg)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }} className="fade-in">
            <h3 style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: '700',
              marginBottom: 'var(--space-lg)',
              color: 'var(--gray-900)'
            }}>
              Payout History
            </h3>

            {(earnings?.payouts?.length > 0 || earnings?.payoutHistory?.length > 0) ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                {(earnings.payouts || earnings.payoutHistory || []).map((payout, index) => {
                  const isSubscriptionDeduction = payout.purpose === 'subscription_deduction';
                  const payoutDate = payout.createdAt || payout.date;

                  return (
                    <div key={payout._id || payout.id || index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: 'var(--space-md)',
                      background: isSubscriptionDeduction ? 'rgba(168, 85, 247, 0.05)' : '#F8FAFC',
                      borderRadius: 'var(--radius-md)',
                      border: isSubscriptionDeduction ? '1px solid rgba(168, 85, 247, 0.2)' : '1px solid #E5E7EB'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                        {/* Icon for subscription deduction */}
                        {isSubscriptionDeduction && (
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            background: 'rgba(168, 85, 247, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '18px',
                            flexShrink: 0
                          }}>🔄</div>
                        )}
                        <div>
                          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', marginBottom: 'var(--space-xs)' }}>
                            {formatDate(payoutDate)}
                          </div>
                          <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: '700', color: isSubscriptionDeduction ? '#a855f7' : 'var(--gray-900)' }}>
                            {isSubscriptionDeduction ? '-' : ''}{formatCurrency(payout.amount)}
                          </div>
                          {isSubscriptionDeduction && (
                            <div style={{ fontSize: 'var(--font-size-xs)', color: '#a855f7', marginTop: '2px' }}>
                              Subscription fee auto-deducted
                            </div>
                          )}
                          {payout.description && !isSubscriptionDeduction && (
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', marginTop: '2px' }}>
                              {payout.description}
                            </div>
                          )}
                          {/* Show if this payout had subscription deducted */}
                          {payout.subscriptionDeduction > 0 && (
                            <div style={{ fontSize: 'var(--font-size-xs)', color: '#a855f7', marginTop: '2px' }}>
                              Subscription deducted: {formatCurrency(payout.subscriptionDeduction)}
                            </div>
                          )}
                          {/* Settlement Fee Breakdown */}
                          {payout.settlementFeeDetails?.applicable && (
                            <div style={{
                              marginTop: '12px',
                              padding: '12px',
                              background: 'rgba(59, 130, 246, 0.05)',
                              borderRadius: '8px',
                              border: '1px solid rgba(59, 130, 246, 0.15)',
                              fontSize: 'var(--font-size-xs)'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                marginBottom: '8px',
                                color: '#3b82f6',
                                fontWeight: '600'
                              }}>
                                📄 {payout.settlementFeeDetails.feeType === 'commission' ? 'Platform Fee' : 'Settlement Fee'} Breakdown
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--gray-600)' }}>
                                  <span>Gross Payout</span>
                                  <span style={{ fontWeight: '500' }}>₹{payout.settlementFeeDetails.payoutAmountBeforeFee?.toFixed(2)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#ef4444' }}>
                                  <span>{payout.settlementFeeDetails.feeType === 'commission' ? 'Platform Fee' : 'Settlement Fee'} ({payout.settlementFeeDetails.settlementFeeRate || 3}%)</span>
                                  <span>-₹{payout.settlementFeeDetails.settlementFee?.toFixed(2)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#ef4444' }}>
                                  <span>GST on Fee ({payout.settlementFeeDetails.gstRate || 18}%)</span>
                                  <span>-₹{payout.settlementFeeDetails.gstOnFee?.toFixed(2)}</span>
                                </div>
                                <div style={{ paddingLeft: '12px', color: 'var(--gray-500)', fontSize: '10px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>• CGST (9%)</span>
                                    <span>₹{payout.settlementFeeDetails.cgst?.toFixed(2)}</span>
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>• SGST (9%)</span>
                                    <span>₹{payout.settlementFeeDetails.sgst?.toFixed(2)}</span>
                                  </div>
                                </div>
                                <div style={{ borderTop: '1px solid rgba(59, 130, 246, 0.2)', paddingTop: '6px', marginTop: '4px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#ef4444', fontWeight: '600' }}>
                                    <span>Total Fee Deducted</span>
                                    <span>-₹{payout.settlementFeeDetails.totalFee?.toFixed(2)}</span>
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#10b981', fontWeight: '600', marginTop: '4px' }}>
                                    <span>Net Received</span>
                                    <span>₹{payout.settlementFeeDetails.netPayoutAfterFee?.toFixed(2)}</span>
                                  </div>
                                </div>
                                {payout.settlementFeeDetails.invoiceNumber && (
                                  <div style={{ color: 'var(--gray-500)', fontStyle: 'italic', marginTop: '6px' }}>
                                    Invoice: {payout.settlementFeeDetails.invoiceNumber}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{
                          padding: 'var(--space-xs) var(--space-md)',
                          borderRadius: 'var(--radius-full)',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: '600',
                          background: isSubscriptionDeduction ? 'rgba(168, 85, 247, 0.1)' :
                                      payout.status === 'success' || payout.status === 'completed' ? 'rgba(16, 185, 129, 0.1)' :
                                      payout.status === 'pending' ? 'rgba(245, 158, 11, 0.1)' :
                                      payout.status === 'processing' ? 'rgba(59, 130, 246, 0.1)' :
                                      'rgba(239, 68, 68, 0.1)',
                          color: isSubscriptionDeduction ? '#a855f7' :
                                 payout.status === 'success' || payout.status === 'completed' ? '#10b981' :
                                 payout.status === 'pending' ? '#f59e0b' :
                                 payout.status === 'processing' ? '#3b82f6' :
                                 '#ef4444'
                        }}>
                          {isSubscriptionDeduction ? 'AUTO RENEWAL' :
                           payout.status === 'success' ? 'Completed' :
                           payout.status?.charAt(0).toUpperCase() + payout.status?.slice(1)}
                        </span>
                        {payout.utr && (
                          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', marginTop: '4px' }}>
                            UTR: {payout.utr}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 'var(--space-2xl)', color: 'var(--gray-600)' }}>
                <div style={{ fontSize: '3rem', marginBottom: 'var(--space-md)' }}>💳</div>
                <p>No payouts yet</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Instant Payout Modal */}
      {showPayoutModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 'var(--space-lg)'
        }} onClick={() => setShowPayoutModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--space-xl)',
            width: '100%',
            maxWidth: '400px',
            maxHeight: '90vh',
            overflow: 'auto'
          }} onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-lg)' }}>
              <h3 style={{ margin: 0, fontSize: 'var(--font-size-xl)', fontWeight: '700', color: 'var(--gray-900)' }}>
                ⚡ Request Instant Payout
              </h3>
              <button
                onClick={() => setShowPayoutModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--gray-500)',
                  padding: '4px'
                }}
              >
                ×
              </button>
            </div>

            {/* Available Balance */}
            <div style={{
              padding: 'var(--space-lg)',
              background: 'rgba(16, 185, 129, 0.1)',
              borderRadius: 'var(--radius-lg)',
              marginBottom: 'var(--space-lg)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', marginBottom: 'var(--space-xs)' }}>
                Available Balance
              </div>
              <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: '800', color: '#10b981' }}>
                {formatCurrency(availableBalance)}
              </div>
            </div>

            {/* Amount Input */}
            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <label style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                fontWeight: '600',
                color: 'var(--gray-700)',
                marginBottom: 'var(--space-sm)'
              }}>
                Enter Amount
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: '600',
                  color: 'var(--gray-500)'
                }}>₹</span>
                <input
                  type="number"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  placeholder={`Min ${instantPayoutInfo?.minAmount || 500}`}
                  onWheel={(e) => e.target.blur()}
                  style={{
                    width: '100%',
                    padding: 'var(--space-md)',
                    paddingLeft: '40px',
                    border: '2px solid #E5E7EB',
                    borderRadius: 'var(--radius-lg)',
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: '600',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#f59e0b'}
                  onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                />
              </div>
              <div style={{
                display: 'flex',
                gap: 'var(--space-sm)',
                marginTop: 'var(--space-sm)'
              }}>
                {[25, 50, 75, 100].map(percent => (
                  <button
                    key={percent}
                    onClick={() => setPayoutAmount(Math.floor((instantPayoutInfo?.maxRequestableAmount || availableBalance) * percent / 100))}
                    style={{
                      flex: 1,
                      padding: 'var(--space-sm)',
                      border: '1px solid #E5E7EB',
                      borderRadius: 'var(--radius-md)',
                      background: 'white',
                      color: 'var(--gray-700)',
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(245, 158, 11, 0.1)';
                      e.target.style.borderColor = '#f59e0b';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'white';
                      e.target.style.borderColor = '#E5E7EB';
                    }}
                  >
                    {percent}%
                  </button>
                ))}
              </div>
            </div>

            {/* Fee Calculation Preview */}
            {payoutAmount > 0 && (
              <div style={{
                padding: 'var(--space-md)',
                background: '#F8FAFC',
                borderRadius: 'var(--radius-lg)',
                marginBottom: 'var(--space-lg)'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 'var(--space-sm)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--gray-600)'
                }}>
                  <span>Requested Amount</span>
                  <span>{formatCurrency(parseFloat(payoutAmount))}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 'var(--space-sm)',
                  fontSize: 'var(--font-size-sm)',
                  color: '#EF4444'
                }}>
                  <span>Processing Fee ({feePercentage}%)</span>
                  <span>- {formatCurrency(calculateFee(payoutAmount))}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: 'var(--space-sm)',
                  borderTop: '1px dashed #E5E7EB',
                  fontSize: 'var(--font-size-md)',
                  fontWeight: '700',
                  color: '#10b981'
                }}>
                  <span>You'll Receive</span>
                  <span>{formatCurrency(calculateNetAmount(payoutAmount))}</span>
                </div>
              </div>
            )}

            {/* Info Note */}
            <div style={{
              padding: 'var(--space-md)',
              background: 'rgba(59, 130, 246, 0.1)',
              borderRadius: 'var(--radius-lg)',
              marginBottom: 'var(--space-lg)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--gray-600)'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)' }}>
                <span style={{ fontSize: '16px' }}>ℹ️</span>
                <div>
                  <p style={{ margin: 0, marginBottom: '4px' }}>
                    • Amount will be transferred within 24 hours
                  </p>
                  <p style={{ margin: 0, marginBottom: '4px' }}>
                    • Min: {formatCurrency(instantPayoutInfo?.minAmount || 500)} | Max: {formatCurrency(instantPayoutInfo?.maxRequestableAmount || availableBalance)}
                  </p>
                  <p style={{ margin: 0 }}>
                    • Admin approval may be required
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleInstantPayout}
              disabled={requestingPayout || !payoutAmount || parseFloat(payoutAmount) < (instantPayoutInfo?.minAmount || 500)}
              style={{
                width: '100%',
                padding: 'var(--space-md)',
                border: 'none',
                borderRadius: 'var(--radius-lg)',
                background: requestingPayout || !payoutAmount || parseFloat(payoutAmount) < (instantPayoutInfo?.minAmount || 500)
                  ? '#E5E7EB' : '#f59e0b',
                color: requestingPayout || !payoutAmount || parseFloat(payoutAmount) < (instantPayoutInfo?.minAmount || 500)
                  ? 'var(--gray-500)' : 'white',
                fontSize: 'var(--font-size-md)',
                fontWeight: '700',
                cursor: requestingPayout || !payoutAmount || parseFloat(payoutAmount) < (instantPayoutInfo?.minAmount || 500)
                  ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {requestingPayout ? (
                <span>Processing...</span>
              ) : (
                <span>⚡ Request Payout</span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      {toasts.length > 0 && (
        <div style={{
          position: 'fixed',
          top: '100px',
          right: '20px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          maxWidth: '400px'
        }}>
          {toasts.map(toast => (
            <div
              key={toast.id}
              style={{
                padding: '16px 20px',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                animation: 'slideIn 0.3s ease',
                background: toast.type === TOAST_TYPES.SUCCESS ? '#10b981' :
                           toast.type === TOAST_TYPES.ERROR ? '#ef4444' :
                           toast.type === TOAST_TYPES.WARNING ? '#f59e0b' : '#3b82f6',
                color: 'white'
              }}
            >
              <span style={{ fontSize: '20px' }}>
                {toast.type === TOAST_TYPES.SUCCESS ? '✅' :
                 toast.type === TOAST_TYPES.ERROR ? '❌' :
                 toast.type === TOAST_TYPES.WARNING ? '⚠️' : 'ℹ️'}
              </span>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: '600', fontSize: '14px' }}>
                  {toast.message}
                </p>
              </div>
              <button
                onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  cursor: 'pointer',
                  color: 'white',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default Earnings;
